import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content
 * and supports the House of Kronaar delivery truck interaction.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Stores active truck animation timeouts per slide.
   * @type {WeakMap<HTMLElement, number>}
   */
  #truckTimeouts = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('click', this.#handleTruckClick);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('click', this.#handleTruckClick);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !interval) return;

    this.suspend();
    this.paused = false;

    this.#interval = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend = () => {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused || document.hidden) return;

    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];

    if (!slides.length) return;

    this.#current = current;

    let relativeIndex = current % slides.length;

    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handleTruckClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const truck = target.closest('[data-announcement-truck]');

    if (!(truck instanceof HTMLElement) || !this.contains(truck)) return;

    event.preventDefault();
    event.stopPropagation();

    const slide = truck.closest('.announcement-bar__slide');

    if (!(slide instanceof HTMLElement)) return;

    const liveMessage = slide.querySelector('[data-announcement-live]');
    const message =
      truck.getAttribute('data-announcement-message') ||
      'Your complimentary delivery is on the way across the USA.';

    if (liveMessage) {
      liveMessage.textContent = message;
    }

    const existingTimeout = this.#truckTimeouts.get(slide);

    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    slide.classList.remove('is-truck-driving');

    void slide.offsetWidth;

    slide.classList.add('is-truck-driving');
    this.suspend();

    const timeout = window.setTimeout(() => {
      slide.classList.remove('is-truck-driving');

      if (liveMessage) {
        liveMessage.textContent = message;
      }

      this.resume();
    }, 3000);

    this.#truckTimeouts.set(slide, timeout);
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
      return;
    }

    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}